<script setup>
import { ref, onMounted, onUnmounted, computed } from 'vue'

const props = defineProps({
    userCurrency: {
        type: String,
        default: 'USD'
    },
    availableCurrencies: {
        type: Array,
        default: () => ['USD', 'BRL', 'EUR', 'GBP', 'JPY', 'TES']
    },
    context: {
        type: String,
        default: 'client',
        validator: (value) => ['admin', 'client'].includes(value)
    }
})

const currentCurrency = ref('USD')
const showMenu = ref(false)

const currencies = computed(() => props.availableCurrencies)

const getCurrencyKey = () => `currency.${props.context}`
const getStoredCurrency = () => localStorage.getItem(getCurrencyKey())
const setStoredCurrency = (currency) => localStorage.setItem(getCurrencyKey(), currency)

const toggleMenu = () => {
    showMenu.value = !showMenu.value
}

const changeCurrency = (code) => {
    currentCurrency.value = code
    setStoredCurrency(code)
    showMenu.value = false

    window.dispatchEvent(
        new CustomEvent('currencyChanged', {
            detail: { currency: code, context: props.context }
        })
    )
    
    window.dispatchEvent(
        new CustomEvent(`currency-changed-${props.context}`, {
            detail: { currency: code }
        })
    )
}

const clickOutside = (event) => {
    const dropdown = document.querySelector('.currency-selector-wrapper')
    if (dropdown && !dropdown.contains(event.target)) {
        showMenu.value = false
    }
}

const handleCurrencyChange = (event) => {
    const newCurrency = event.detail.currency
    if (props.availableCurrencies.includes(newCurrency)) {
        currentCurrency.value = newCurrency
    }
}

onMounted(() => {
    const storedCurrency = getStoredCurrency()
    
    if (storedCurrency && props.availableCurrencies.includes(storedCurrency)) {
        currentCurrency.value = storedCurrency
    } else {
        currentCurrency.value = props.userCurrency
        setStoredCurrency(props.userCurrency)
    }

    const eventName = `currency-changed-${props.context}`
    window.addEventListener(eventName, handleCurrencyChange)
    document.addEventListener('click', clickOutside)
})

onUnmounted(() => {
    window.removeEventListener(`currency-changed-${props.context}`, handleCurrencyChange)
    document.removeEventListener('click', clickOutside)
})
</script>

<template>
    <div class="currency-selector-wrapper">
        <!-- Main button -->
        <div class="currency-button" @click="toggleMenu">
            <span class="text-sm">{{ currentCurrency }}</span>
        </div>

        <!-- Dropdown -->
        <transition name="fade-scale">
            <div v-if="showMenu" class="currency-menu">
                <div v-for="currency in currencies" :key="currency" class="currency-option"
                    @click="changeCurrency(currency)">
                    {{ currency }}
                </div>
            </div>
        </transition>
    </div>
</template>

<style scoped>
.currency-selector-wrapper {
    position: relative;
    width: 40px;
}

.currency-button {
    width: 40px;
    height: 40px;
    border: 1px solid #e5e7eb;
    border-radius: 6px;
    background: white;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    transition: all 0.2s ease;
}

.currency-button:hover {
    background: #f9fafb;
}

.currency-menu {
    position: absolute;
    top: 50px;
    left: 0;
    width: 55px;
    background: white;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
    z-index: 50;
}

.currency-option {
    padding: 12px 0;
    text-align: center;
    font-size: 0.875rem;
    cursor: pointer;
    transition: background 0.2s;
}

.currency-option:hover {
    background: #f3f4f6;
}

/* Animation */
.fade-scale-enter-active,
.fade-scale-leave-active {
    transition: all 0.15s ease-out;
}

.fade-scale-enter-from {
    opacity: 0;
    transform: scale(0.95) translateY(-4px);
}

.fade-scale-enter-to {
    opacity: 1;
    transform: scale(1) translateY(0);
}

.fade-scale-leave-from {
    opacity: 1;
    transform: scale(1) translateY(0);
}

.fade-scale-leave-to {
    opacity: 0;
    transform: scale(0.95) translateY(-4px);
}
</style>